/* AMAR — bakhoor smoke engine
 * ---------------------------------------------------------------------------
 * Volumetric incense smoke rendered on a 2D canvas. No images, no video, no
 * library. Particles are advected through a curl-noise field (divergence-free,
 * so the flow swirls like real smoke instead of sliding like fog), lifted by
 * buoyancy that decays as each puff "cools", and drawn additively in ash tones.
 *
 * Performance notes:
 *  - The buffer renders at ~40% of CSS size and is upscaled by the browser.
 *    Smoke is soft by nature, so the resample is invisible and the fill cost
 *    drops by ~6x.
 *  - Sprites are pre-rendered once per colour bucket; per-frame work is a
 *    drawImage loop, no gradients created in the render path.
 *  - Frame rate is capped (smoke moves slowly; 60fps buys nothing but battery
 *    drain), and the loop stops entirely when the section leaves the viewport
 *    or the tab is hidden.
 *  - prefers-reduced-motion renders one settled frame and stops, unless the
 *    section explicitly asks for motion via data-motion="always".
 * ------------------------------------------------------------------------- */
(function () {
  'use strict';

  var PREFERS_REDUCED =
    window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var COARSE_POINTER =
    window.matchMedia && window.matchMedia('(pointer: coarse)').matches;

  /* --------------------------------------------------------------------- */
  /* 2D simplex noise — used as a scalar potential field for curl advection */
  /* --------------------------------------------------------------------- */
  function Noise(seed) {
    var p = new Uint8Array(256);
    var perm = new Uint8Array(512);
    var i, n, tmp;

    for (i = 0; i < 256; i++) p[i] = i;

    var s = (seed >>> 0) || 1;
    function rnd() {
      s ^= s << 13; s >>>= 0;
      s ^= s >> 17;
      s ^= s << 5;  s >>>= 0;
      return s / 4294967296;
    }
    for (i = 255; i > 0; i--) {
      n = Math.floor(rnd() * (i + 1));
      tmp = p[i]; p[i] = p[n]; p[n] = tmp;
    }
    for (i = 0; i < 512; i++) perm[i] = p[i & 255];

    var F2 = 0.5 * (Math.sqrt(3) - 1);
    var G2 = (3 - Math.sqrt(3)) / 6;
    var g = [[1, 1], [-1, 1], [1, -1], [-1, -1], [1, 0], [-1, 0], [0, 1], [0, -1]];

    this.n2 = function (xin, yin) {
      var sk = (xin + yin) * F2;
      var i0 = Math.floor(xin + sk);
      var j0 = Math.floor(yin + sk);
      var t0 = (i0 + j0) * G2;
      var x0 = xin - (i0 - t0);
      var y0 = yin - (j0 - t0);
      var i1, j1;
      if (x0 > y0) { i1 = 1; j1 = 0; } else { i1 = 0; j1 = 1; }
      var x1 = x0 - i1 + G2, y1 = y0 - j1 + G2;
      var x2 = x0 - 1 + 2 * G2, y2 = y0 - 1 + 2 * G2;
      var ii = i0 & 255, jj = j0 & 255;
      var gi0 = perm[ii + perm[jj]] & 7;
      var gi1 = perm[ii + i1 + perm[jj + j1]] & 7;
      var gi2 = perm[ii + 1 + perm[jj + 1]] & 7;
      var n0 = 0, n1 = 0, n2 = 0, t;

      t = 0.5 - x0 * x0 - y0 * y0;
      if (t > 0) { t *= t; n0 = t * t * (g[gi0][0] * x0 + g[gi0][1] * y0); }
      t = 0.5 - x1 * x1 - y1 * y1;
      if (t > 0) { t *= t; n1 = t * t * (g[gi1][0] * x1 + g[gi1][1] * y1); }
      t = 0.5 - x2 * x2 - y2 * y2;
      if (t > 0) { t *= t; n2 = t * t * (g[gi2][0] * x2 + g[gi2][1] * y2); }

      return 70 * (n0 + n1 + n2);
    };
  }

  /* --------------------------------------------------------------------- */
  /* Colour ramp: ash only, from dim smoke at the source to pale smoke as it */
  /* thins. Deliberately neutral. An amber value at the hot end reads as a  */
  /* flame under the plume once additive blending stacks it, which is not   */
  /* what bakhoor looks like: the coal is covered, the smoke is grey.       */
  /* --------------------------------------------------------------------- */
  var RAMP = [
    [150, 147, 142],
    [172, 169, 163],
    [192, 189, 183],
    [208, 205, 199],
    [220, 218, 212],
    [232, 230, 225]
  ];

  var SPRITE_PX = 128;

  function buildSprites() {
    return RAMP.map(function (rgb) {
      var c = document.createElement('canvas');
      c.width = c.height = SPRITE_PX;
      var x = c.getContext('2d');
      var half = SPRITE_PX / 2;
      var grd = x.createRadialGradient(half, half, 0, half, half, half);
      var col = rgb[0] + ',' + rgb[1] + ',' + rgb[2];
      /* A soft, fat-tailed falloff. A plain linear gradient reads as a bubble;
         the extra midpoints give the puff a diffuse edge with a denser core. */
      grd.addColorStop(0.00, 'rgba(' + col + ',0.42)');
      grd.addColorStop(0.18, 'rgba(' + col + ',0.26)');
      grd.addColorStop(0.42, 'rgba(' + col + ',0.10)');
      grd.addColorStop(0.70, 'rgba(' + col + ',0.028)');
      grd.addColorStop(1.00, 'rgba(' + col + ',0)');
      x.fillStyle = grd;
      x.fillRect(0, 0, SPRITE_PX, SPRITE_PX);
      return c;
    });
  }

  var SPRITES = null;

  /* --------------------------------------------------------------------- */
  /* The smoke instance                                                     */
  /* --------------------------------------------------------------------- */
  function Smoke(canvas) {
    this.canvas = canvas;
    this.ctx = canvas.getContext('2d', { alpha: true });
    if (!this.ctx) return;

    var d = canvas.dataset;
    this.density = parseFloat(d.density) || 1;
    this.interactive = d.interactive !== 'false' && !COARSE_POINTER;
    this.speed = parseFloat(d.speed) || 1;

    /* data-motion="always" overrides the OS reduced-motion preference. The
       plume is slow, low-contrast and never flashes, so a merchant may
       reasonably want it running for everyone; "respect" is still available
       in the section settings. */
    this.reduced = PREFERS_REDUCED && d.motion !== 'always';

    this.noise = new Noise(0x9e3779b9);
    this.parts = [];
    this.running = false;
    this.visible = false;
    this.settled = false;
    this.time = 0;
    this.lastTs = 0;
    this.accum = 0;
    this.ignition = 0;          /* 0 -> 1 ramp when the section is first seen */
    this.pointer = { x: -9999, y: -9999, active: false };

    this.resize();
    this.seedParticles();

    var self = this;

    this._onResize = debounce(function () {
      self.resize();
      self.seedParticles();
      if (!self.running && self.settled) self.renderStill();
    }, 220);
    window.addEventListener('resize', this._onResize, { passive: true });

    if (this.interactive) {
      this._onMove = function (e) {
        var r = self.canvas.getBoundingClientRect();
        if (!r.width || !r.height) return;
        self.pointer.x = ((e.clientX - r.left) / r.width) * self.w;
        self.pointer.y = ((e.clientY - r.top) / r.height) * self.h;
        self.pointer.active = true;
      };
      this._onLeave = function () { self.pointer.active = false; };
      var host = canvas.closest('.amem') || canvas.parentNode;
      host.addEventListener('pointermove', this._onMove, { passive: true });
      host.addEventListener('pointerleave', this._onLeave, { passive: true });
    }

    this._onVis = function () {
      if (document.hidden) self.stop();
      else if (self.visible) self.start();
    };
    document.addEventListener('visibilitychange', this._onVis);

    if ('IntersectionObserver' in window) {
      this.io = new IntersectionObserver(function (entries) {
        entries.forEach(function (en) {
          self.visible = en.isIntersecting;
          if (en.isIntersecting) {
            if (self.reduced) { self.renderStill(); self.io.disconnect(); }
            else self.start();
          } else {
            self.stop();
          }
        });
      }, { rootMargin: '12% 0px 12% 0px', threshold: 0 });
      this.io.observe(canvas);
    } else if (this.reduced) {
      this.renderStill();
    } else {
      this.visible = true;
      this.start();
    }

    /* Safety net. If the observer never delivers (a stale polyfill, a display
       quirk, a section injected outside the normal lifecycle) the section
       would sit frozen with no way to recover. */
    setTimeout(function () {
      if (self.running || self.settled || self.reduced) return;
      var r = self.canvas.getBoundingClientRect();
      if (r.bottom > 0 && r.top < (window.innerHeight || 0)) {
        self.visible = true;
        self.start();
      }
    }, 2000);
  }

  Smoke.prototype.resize = function () {
    var rect = this.canvas.getBoundingClientRect();
    var cssW = Math.max(1, Math.round(rect.width || this.canvas.offsetWidth || 1));
    var cssH = Math.max(1, Math.round(rect.height || this.canvas.offsetHeight || 1));

    /* Render small, upscale wide. Smoke has no hard edges to lose. */
    var scale = cssW > 1100 ? 0.36 : cssW > 700 ? 0.42 : 0.5;
    this.w = Math.max(2, Math.round(cssW * scale));
    this.h = Math.max(2, Math.round(cssH * scale));
    this.canvas.width = this.w;
    this.canvas.height = this.h;

    /* Physics is authored against a 400px-tall reference so plumes keep their
       proportions whatever the section height or the buffer scale. */
    this.unit = this.h / 400;
  };

  /* Density matters more than size here: smoke only reads as a medium when the
     puffs overlap heavily. Few large sprites look like bokeh, not bakhoor. */
  Smoke.prototype.count = function () {
    var area = (this.w * this.h) / (520 * 320);
    var base = COARSE_POINTER ? 160 : 320;
    return Math.max(60, Math.min(380, Math.round(base * area * this.density)));
  };

  Smoke.prototype.spawn = function (p, stagger) {
    /* An ember bed just below the frame, like a mabkhara out of shot. The
       triangular spread keeps the base narrow and centre-weighted. */
    var ex = 0.5 + (Math.random() + Math.random() - 1) * 0.14;
    var u = this.unit;

    /* Two classes of puff. The body carries the mass, the wisps carry the
       detail. A single size always collapses into either fog or bokeh. */
    var fine = Math.random() < 0.62;

    p.x = ex * this.w;
    p.y = this.h * (1.0 + Math.random() * 0.14);
    p.vx = (Math.random() - 0.5) * 4 * u;
    p.vy = -(24 + Math.random() * 18) * u;
    p.life = stagger ? Math.random() : 0;

    if (fine) {
      /* Wisps. Kept large enough to read as torn edges of the plume. */
      p.max = 7 + Math.random() * 5.5;
      p.r0 = (11 + Math.random() * 13) * u;
      p.rg = (44 + Math.random() * 48) * u;
      p.gain = 1.45;
    } else {
      /* Billows. Growth is capped: a puff that keeps expanding for its whole
         life ends up a huge pale disc, and a cluster of those is a blob. */
      p.max = 10.5 + Math.random() * 8;
      p.r0 = (24 + Math.random() * 28) * u;
      p.rg = (80 + Math.random() * 86) * u;
      p.gain = 0.98;
    }

    p.amp = (fine ? 0.85 : 0.5) + Math.random() * 0.6;
    p.rot = Math.random() * Math.PI * 2;
    p.spin = (Math.random() - 0.5) * 0.22;
    p.seed = Math.random() * 1000;
    return p;
  };

  Smoke.prototype.seedParticles = function () {
    var n = this.count();
    var arr = new Array(n);
    for (var i = 0; i < n; i++) arr[i] = this.spawn({}, true);
    this.parts = arr;
  };

  /* Curl of a scalar noise potential => divergence-free velocity field.
     Two octaves: the coarse one carries the big lazy swirls, the fine one
     frays the edges into wisps. One octave alone looks like drifting fog. */
  Smoke.prototype.curl = function (x, y, t, out) {
    var n = this.noise;
    var e = 1.4;
    var s1 = 0.0042 / this.unit;
    var yd1 = t * 0.055;
    var a1 = (n.n2(x * s1, (y + e) * s1 - yd1) - n.n2(x * s1, (y - e) * s1 - yd1)) / (2 * e);
    var b1 = -(n.n2((x + e) * s1, y * s1 - yd1) - n.n2((x - e) * s1, y * s1 - yd1)) / (2 * e);

    var s2 = s1 * 3.1;
    var yd2 = t * 0.13 + 41.7;
    var a2 = (n.n2(x * s2, (y + e) * s2 - yd2) - n.n2(x * s2, (y - e) * s2 - yd2)) / (2 * e);
    var b2 = -(n.n2((x + e) * s2, y * s2 - yd2) - n.n2((x - e) * s2, y * s2 - yd2)) / (2 * e);

    out[0] = a1 + a2 * 0.42;
    out[1] = b1 + b2 * 0.42;
  };

  var _c = [0, 0];

  Smoke.prototype.step = function (dt) {
    this.time += dt;
    var t = this.time;
    var u = this.unit;

    /* A slow swell, like breathing. Period is deliberately prime-ish against
       the noise drift so the two never visibly sync up. */
    var breath = 1 + Math.sin(t * 0.19) * 0.16 + Math.sin(t * 0.071) * 0.09;

    var ptr = this.pointer;
    var ptrR = 150 * u;
    var ptrR2 = ptrR * ptrR;

    for (var i = 0; i < this.parts.length; i++) {
      var p = this.parts[i];

      p.life += dt / p.max;
      if (p.life >= 1) { this.spawn(p, false); continue; }

      var age = p.life;

      this.curl(p.x, p.y, t + p.seed, _c);
      /* Laminar at the ember, turbulent above it. Real incense leaves the coal
         as a near-still ribbon, holds that line for a few centimetres, then
         trips into billows. */
      var turb = 120 * u * p.amp * (0.1 + Math.pow(age, 1.7) * 1.85);
      /* Sideways swirl is suppressed while the ribbon is young and released as
         it ages, so the column is tight at the source and open at the top. */
      p.vx += _c[0] * turb * dt * (0.5 + age * 1.15);
      p.vy += _c[1] * turb * dt;

      /* Entrainment: cooling smoke rolls outward from the axis it rose along.
         This is what gives a plume its cone. */
      p.vx += (p.x >= this.w * 0.5 ? 1 : -1) * age * age * 30 * u * dt;

      /* Buoyancy fades as the puff cools and spreads. */
      var lift = (0.3 + 0.7 * Math.pow(1 - age, 1.6)) * 128 * u * breath;
      p.vy -= lift * dt;

      /* A whisper of lateral drift so the column leans, never rises dead straight. */
      p.vx += Math.sin(t * 0.23 + p.seed) * 5 * u * dt;

      if (ptr.active) {
        var dx = p.x - ptr.x, dy = p.y - ptr.y;
        var d2 = dx * dx + dy * dy;
        if (d2 < ptrR2 && d2 > 0.01) {
          var d = Math.sqrt(d2);
          var f = (1 - d / ptrR);
          f = f * f * 150 * u;
          p.vx += (dx / d) * f * dt;
          p.vy += (dy / d) * f * dt;
        }
      }

      var drag = Math.exp(-1.05 * dt);
      p.vx *= drag;
      p.vy *= drag;

      p.x += p.vx * dt;
      p.y += p.vy * dt;
      p.rot += p.spin * dt;

      /* Recycled as soon as it clears the frame. Letting spent puffs live on
         above the top edge is what fed the cloud that used to gather there. */
      if (p.y < -this.h * 0.04 || p.x < -this.w * 0.4 || p.x > this.w * 1.4) {
        this.spawn(p, false);
      }
    }
  };

  Smoke.prototype.draw = function () {
    var ctx = this.ctx;
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, this.w, this.h);
    ctx.globalCompositeOperation = 'lighter';

    var ign = this.ignition;
    var last = RAMP.length - 1;

    for (var i = 0; i < this.parts.length; i++) {
      var p = this.parts[i];
      var age = p.life;
      if (age <= 0 || age >= 1) continue;

      /* Fade in over the first fifth of life, then decay to nothing by 0.9.
         The old curve still had visible alpha at the end of life, which let
         spent puffs survive long enough to reach the ceiling. */
      var a = age < 0.2 ? age / 0.2 : 1 - (age - 0.2) / 0.7;
      if (a < 0) a = 0;
      a = a * a * (3 - 2 * a);            /* smoothstep — no linear pops */
      a *= 0.24 * ign * (p.gain || 1);

      /* Dissipation with height. Real smoke thins as it climbs and is gone
         well before any ceiling. Without this, long-lived puffs pool against
         the top of the section into one bright cloud: the plume stops being
         a plume and becomes a smear. Full strength through the lower half,
         squared falloff above it, nothing left in the top eighth. */
      var hf = p.y / this.h;              /* 1 at the ember, 0 at the top */
      if (hf < 0.42) {
        if (hf <= 0.02) continue;
        var vf = (hf - 0.02) / 0.4;
        a *= vf * vf;
      }

      if (a <= 0.004) continue;

      var r = p.r0 + p.rg * Math.pow(age, 0.68);
      var sprite = SPRITES[Math.min(last, (age * last + 0.35) | 0)];

      /* Stretch each puff along its own velocity. This is the difference
         between a field of soft circles and something that reads as smoke:
         real puffs are sheared by the flow they are travelling through. */
      var sp = Math.sqrt(p.vx * p.vx + p.vy * p.vy);
      var ang = sp > 0.001 ? Math.atan2(p.vy, p.vx) : p.rot;
      var stretch = 1 + Math.min(1, sp / (70 * this.unit)) * 0.85;

      ctx.globalAlpha = a;
      ctx.setTransform(Math.cos(ang), Math.sin(ang), -Math.sin(ang), Math.cos(ang), p.x, p.y);
      ctx.drawImage(sprite, -r * stretch, -r, r * 2 * stretch, r * 2);
    }

    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.globalAlpha = 1;
    ctx.globalCompositeOperation = 'source-over';
  };

  /* One settled frame, no animation — the reduced-motion path. */
  Smoke.prototype.renderStill = function () {
    if (this.settled) return;
    this.ignition = 1;
    for (var i = 0; i < 150; i++) this.step(1 / 30);
    this.draw();
    this.settled = true;
  };

  Smoke.prototype.start = function () {
    if (this.running || this.reduced || document.hidden) return;
    this.running = true;

    /* Pre-roll so the section is never caught empty, but keep it short: the
       last of the build-up happens on screen. */
    if (!this.warmed) {
      for (var i = 0; i < 90; i++) this.step(1 / 24);
      this.warmed = true;
    }

    var self = this;
    this.lastTs = 0;
    this.accum = 0;

    var FRAME = 1 / 44;  /* smoke is slow; 44fps is indistinguishable from 60 */

    function loop(ts) {
      if (!self.running) return;
      self.raf = requestAnimationFrame(loop);

      if (!self.lastTs) { self.lastTs = ts; return; }
      var dt = (ts - self.lastTs) / 1000;
      self.lastTs = ts;
      if (dt > 0.1) dt = 0.1;               /* tab-switch guard */
      dt *= self.speed;

      self.ignition = Math.min(1, self.ignition + dt / 2.2);

      self.accum += dt;
      if (self.accum < FRAME) return;
      var stepDt = self.accum;
      self.accum = 0;

      self.step(stepDt);
      self.draw();
    }

    this.raf = requestAnimationFrame(loop);
  };

  Smoke.prototype.stop = function () {
    this.running = false;
    if (this.raf) cancelAnimationFrame(this.raf);
    this.raf = null;
    this.lastTs = 0;
  };

  Smoke.prototype.destroy = function () {
    this.stop();
    if (this.io) this.io.disconnect();
    window.removeEventListener('resize', this._onResize);
    document.removeEventListener('visibilitychange', this._onVis);
  };

  function debounce(fn, ms) {
    var t;
    return function () {
      clearTimeout(t);
      t = setTimeout(fn, ms);
    };
  }

  /* --------------------------------------------------------------------- */
  /* Boot — idempotent, so theme-editor section reloads never double-run.   */
  /* --------------------------------------------------------------------- */
  var instances = [];

  function init() {
    var nodes = document.querySelectorAll('canvas[data-amar-smoke]');
    if (!nodes.length) return;
    if (!SPRITES) SPRITES = buildSprites();

    Array.prototype.forEach.call(nodes, function (c) {
      if (c.__amarSmoke) return;
      try {
        c.__amarSmoke = new Smoke(c);
        instances.push(c.__amarSmoke);
      } catch (e) {
        /* Canvas unavailable — the CSS lift layer still carries the mood. */
      }
    });
  }

  window.AmarSmoke = { init: init, instances: instances };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', function (e) {
    var old = e.target.querySelector('canvas[data-amar-smoke]');
    if (old && old.__amarSmoke) old.__amarSmoke.destroy();
    init();
  });
  document.addEventListener('shopify:section:unload', function (e) {
    var c = e.target.querySelector('canvas[data-amar-smoke]');
    if (c && c.__amarSmoke) c.__amarSmoke.destroy();
  });
})();
